import { Button, styled, Typography } from '@mui/material';
import {
  Add,
  DeleteOutline,
  Download,
  Edit,
  Input,
  MoreHoriz,
  UploadFile,
} from '@mui/icons-material';
import { Filter } from 'components/new/PageFilters';
import PageLoader from 'components/PageLoader';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import { useLayoutContext } from 'contexts/LayoutContext';
import api, { type ApiResponses } from 'lib/trpcClient';
import AddNewDialog, { type AccountData } from './AddNewDialog';
import { type TransactionTypeData } from './TransactionTypeFormContent';
import { useDisclosure, useFilters } from 'further-ui/hooks';
import TransactionTypesTable from './TransactionTypesTable';
import useAccountsManagement from 'hooks/lp/data/accounts/useAccountsManagement';
import { useMemo, useRef, useState } from 'react';
import {
  convertFileToBase64Content,
  numberToCurrencyString,
} from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import ImportErrorsDialog from './ImportErrorsDialog';
import { useNotification } from 'hooks/ui/useNotification';
import { Column, Row } from 'further-ui/components/Layout';
import { AccountSectionLabels } from 'further-ui/labels';
import Menu from 'further-ui/components/Menu';
import { Schema as AccountSchema } from 'further-types/lp/account';

type AccountListResponse = ApiResponses['account']['listAllAccounts'];
type AccountRow = AccountListResponse[keyof AccountListResponse][number];

const IndicatorPadding = styled('div')(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs),
}));

const columns: Array<TableColumn<AccountRow>> = [
  {
    key: 'accountCode',
    label: 'Code',
    width: 250,
    render: (row) => {
      const sectionLabel = AccountSectionLabels[row.section];
      return (
        <Row centered spacing="xs">
          <IndicatorPadding>
            <ColorIndicator colorKey={{ name: sectionLabel }} />
          </IndicatorPadding>
          {row.accountCode}
          <Typography variant="body2" color="text.secondary">
            {sectionLabel}
          </Typography>
        </Row>
      );
    },
  },
  {
    key: 'accountName',
    label: 'Account',
    width: 200,
    render: (row) => row.accountName,
  },
  {
    key: 'runningBalance',
    label: 'Running balance',
    width: 150,
    render: (row) => numberToCurrencyString(row.runningBalance),
  },
  {
    key: 'transactionTypes',
    label: 'Transaction types',
    width: 150,
    render: (row) => row.transactionTypes.length,
  },
];

const pageId = 'fund-setup-chart-of-accounts';

const ChartOfAccounts: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const [canCreate, canEdit, canDelete] = useHasPermissions([
    RolePermission.LpCreateAccount,
    RolePermission.LpEditAccount,
    RolePermission.LpDeleteAccount,
  ]);
  const inputFile = useRef<HTMLInputElement>(null);
  const [importErrors, setImportErrors] = useState<{
    transactionTypeIdsInUse?: Array<string>;
    accountCodeNameConflicts?: Array<{
      accountCode: string;
      accountNames: Array<string>;
    }>;
    accountNameCodeConflicts?: Array<{
      accountName: string;
      accountCodes: Array<string>;
    }>;
  } | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());

  const addNewDisclosure = useDisclosure<{
    accountId?: string;
    selectedAccountId?: string;
    transactionType?: TransactionTypeData;
    defaultToTransactionTypeTab?: boolean;
  }>();
  const errorDialogDisclosure = useDisclosure();
  const notification = useNotification();
  const { filters, handleChangeFilters } = useFilters(pageId, {
    defaultFilters: {
      searchQuery: '',
    },
  });
  const { deleteAccount, exportAccounts } = useAccountsManagement();

  const apiUtils = api.useUtils();

  type ImportResult = {
    success: boolean;
    errors: {
      transactionTypeIdsInUse?: Array<string>;
      accountCodeNameConflicts?: Array<{
        accountCode: string;
        accountNames: Array<string>;
      }>;
      accountNameCodeConflicts?: Array<{
        accountName: string;
        accountCodes: Array<string>;
      }>;
    } | null;
  };

  const importTransactionTypes = api.transactionType.importList.useMutation(
    useHandleTrpcMutation<ImportResult>()({
      successMessage: '',
      refetchType: 'none',
      onSuccess: async (result: ImportResult) => {
        if (result.success) {
          notification.success('Transaction types imported successfully.');
          await apiUtils.invalidate();
        } else if (result.errors) {
          setImportErrors(result.errors);
          errorDialogDisclosure.onOpen();
        }
      },
    }),
  );

  const accounts = api.account.listAllAccounts.useQuery({
    vehicleId: selectedVehicleId ?? '',
    searchQuery: filters.searchQuery,
  });

  const hasAnyAccounts = useMemo(() => {
    return Object.values(accounts.data ?? {}).flat().length > 0;
  }, [accounts.data]);

  const {
    mutate: copyAccountsAndMappings,
    isPending: isCopyingAccountsAndMappings,
  } = api.vehicle.copyAccountsAndMappings.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Accounts and mappings added successfully.',
    }),
  );

  const flatRows = useMemo(() => {
    return Object.values(accounts.data ?? {}).flat();
  }, [accounts.data]);

  if (accounts.isLoading) {
    return <PageLoader />;
  }

  const allAccounts = Object.values(accounts.data ?? []).flat();

  const selectedAccount = allAccounts.find(
    (account) => account.id === addNewDisclosure.actionProps?.accountId,
  );

  const prepaidExpenseAccountId = allAccounts.find(
    (account) =>
      account.payableReceivableCategory ===
      AccountSchema.PayableReceivableCategory.PrepaidExpenses,
  )?.id;
  const deferredIncomeAccountId = allAccounts.find(
    (account) =>
      account.payableReceivableCategory ===
      AccountSchema.PayableReceivableCategory.DeferredIncome,
  )?.id;
  const bankTransferReceivableAccountId = allAccounts.find(
    (account) =>
      account.payableReceivableCategory ===
      AccountSchema.PayableReceivableCategory.ReceivableBankTransfer,
  )?.id;

  const handleDeleteAccount = (id: string) => {
    deleteAccount.mutate({
      id,
      vehicleId: selectedVehicleId ?? '',
    });
  };

  const handleEditAccount = (accountId: string) => {
    addNewDisclosure.stageAction({ accountId });
    addNewDisclosure.onOpen();
  };

  const handleEditTransactionType = (transactionType: TransactionTypeData) => {
    addNewDisclosure.stageAction({ transactionType });
    addNewDisclosure.onOpen();
  };

  const handleAddTransactionType = (accountId: string) => {
    addNewDisclosure.stageAction({
      defaultToTransactionTypeTab: true,
      selectedAccountId: accountId,
    });
    addNewDisclosure.onOpen();
  };

  const handleRowExpand = (row: (typeof flatRows)[number]) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      return next;
    });
  };

  const handleExport = () => {
    exportAccounts.mutate({
      vehicleId: selectedVehicleId ?? '',
    });
  };

  const handleImport = async () => {
    const file = inputFile.current?.files?.[0];

    if (!file) return;

    const base64File = await convertFileToBase64Content(file);

    const result = await importTransactionTypes.mutateAsync({
      file: base64File,
      vehicleId: selectedVehicleId ?? '',
      filename: file.name,
    });

    if (result && !result.success && result.errors) {
      setImportErrors(result.errors);
      errorDialogDisclosure.onOpen();
    }
  };

  const handleCloseErrorDialog = () => {
    errorDialogDisclosure.onClose();
    setImportErrors(null);
  };

  const editAccount: AccountData | undefined = selectedAccount
    ? {
        ...selectedAccount,
        balanceSheetSection: selectedAccount.balanceSheetSection ?? undefined,
        isCashAccount: selectedAccount.isCashAccount,
      }
    : undefined;

  return (
    <Column spacing="lg">
      <Row
        centered
        spacing="sm"
        fullWidth
        breakpoint="md"
        justify="space-between"
      >
        <Filter size="md">
          <SearchField
            autoSearchOnDebounce
            placeholder="Search by code or account name"
            fullWidth
            value={filters.searchQuery}
            onSearch={(searchQuery) => {
              handleChangeFilters({ searchQuery });
            }}
            allowEmptyQuery
            iconPlacement="right"
          />
        </Filter>
        <Row spacing="sm" centered fullWidth justify="flex-end">
          {canCreate && (
            <Button
              variant="contained"
              size="large"
              onClick={() => addNewDisclosure.onOpen()}
            >
              Add new
            </Button>
          )}
          <Menu
            handleVariant="square"
            handleIcon={MoreHoriz}
            loading={exportAccounts.isPending}
            items={[
              ...(!hasAnyAccounts && canCreate
                ? [
                    {
                      label: 'Use default accounts/mappings',
                      icon: Input,
                      disabled: isCopyingAccountsAndMappings,
                      onClick: () =>
                        copyAccountsAndMappings({
                          vehicleId: selectedVehicleId ?? '',
                          useFurtherDefaults: true,
                        }),
                    },
                  ]
                : []),
              {
                label: 'Export',
                icon: Download,
                onClick: handleExport,
              },
              ...(canCreate
                ? [
                    {
                      label: 'Import',
                      icon: UploadFile,
                      onClick: () => inputFile.current?.click(),
                    },
                  ]
                : []),
            ]}
          />
        </Row>
      </Row>
      <input
        name="importTransactionTypesFile"
        type="file"
        ref={inputFile}
        hidden
        onChange={handleImport}
        onClick={(e) => {
          if (e.currentTarget) {
            e.currentTarget.value = '';
          }
        }}
      />
      <Table
        columns={columns}
        rows={flatRows}
        isLoading={accounts.isFetching}
        scrollable
        disableSorting
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRowIds.has(row.id)}
        onRowExpand={handleRowExpand}
        onRowClick={handleRowExpand}
        getExpandedContent={(row) => (
          <TransactionTypesTable
            vehicleId={selectedVehicleId ?? ''}
            accountId={row.id ?? ''}
            transactionTypes={row.transactionTypes}
            onEditTransactionType={handleEditTransactionType}
          />
        )}
        actions={[
          {
            label: 'Add transaction type',
            icon: Add,
            hidden: !canCreate,
            onClick: (row) => handleAddTransactionType(row.id),
          },
          {
            label: 'Edit',
            icon: Edit,
            hidden: !canEdit,
            onClick: (row) => handleEditAccount(row.id),
          },
          {
            label: 'Delete',
            icon: DeleteOutline,
            hidden: !canDelete,
            disabled: (row) => row.transactionTypes.length > 0,
            tooltip: (row) =>
              row.transactionTypes.length > 0
                ? 'This account cannot be deleted while active transaction types exist within it.'
                : undefined,
            onClick: (row) => handleDeleteAccount(row.id),
          },
        ]}
      />
      {addNewDisclosure.isOpen && (
        <AddNewDialog
          onClose={addNewDisclosure.onClose}
          vehicleId={selectedVehicleId ?? ''}
          account={editAccount}
          selectedAccountId={addNewDisclosure.actionProps?.selectedAccountId}
          transactionType={addNewDisclosure.actionProps?.transactionType}
          defaultToTransactionTypeTab={
            addNewDisclosure.actionProps?.defaultToTransactionTypeTab
          }
          prepaidExpenseAccountId={prepaidExpenseAccountId}
          deferredIncomeAccountId={deferredIncomeAccountId}
          bankTransferReceivableAccountId={bankTransferReceivableAccountId}
        />
      )}
      <ImportErrorsDialog
        open={errorDialogDisclosure.isOpen}
        onClose={handleCloseErrorDialog}
        errors={importErrors}
      />
    </Column>
  );
};

export default ChartOfAccounts;
